import React, { ForwardedRef } from 'react'
import { useBufferedUpdates } from '../../hooks/hooks'
import { ButtonStyle } from '../simple/button'
import { IconTimes } from '../../icons/library'
import { format } from 'date-fns-tz'
import { isNullOrEmpty } from '../../utils'
import { isEmail, isMobilePhone, isURL } from 'validator'

interface TextInputProps {
  /** The placeholder text for the input. */
  placeholder?: string
  /** Additional CSS classes for the input. */
  className?: string
  /** Callback function triggered when the input value changes. */
  onChange?: (v: string) => void
  /** Callback function triggered when the input is submitted (Enter key). */
  onSubmit?: () => void
  /** Callback function triggered when the input gains or loses focus. */
  onFocusChange?: (f: boolean) => void
  /** Callback function triggered on key down events, besides Enter (that triggers onSubmit). */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /** The current value of the input. */
  value: string
  /** If true, the input is disabled, preventing user interaction. */
  disabled?: boolean
  /** If true, the input is smaller in size. */
  small?: boolean
  /** If true, the input has no outline ever (used for custom styling in a wrapper). */
  nooutline?: boolean
  /** An icon to display inside the input, on the left side. */
  icon?: React.ReactNode
  /** Additional icons to display on the right side of the input. These float over content. */
  rightIcons?: React.ReactNode
  /** Callback function triggered when text is pasted into the input. */
  onPaste?: (value: string) => boolean
  /** If true, the input is marked as invalid, and has a red outline. */
  invalid?: boolean
  /** If true, a clear button is shown when the input has a value. */
  clearable?: boolean
  /** Shorthand for a blue input style. */
  blue?: boolean
  /** The type of the input (text, number, date). This may be used by the browser to improve the interface for filling in that data type. */
  type?: 'text' | 'number' | 'date'
  /** The name of the autocomplete field. This is used by the browser autofill previously entered values. */
  autocomplete?: string
  /** If true, the input grows to fit the content inside it. This is done by measuring the text width and setting the input width accordingly. */
  growToContent?: boolean
  /** The type of validation to perform on the input value (email, phone, url). This is used to determine if the input is valid or not. */
  validation?: 'email' | 'phone' | 'url'
  /** Callback function triggered when the validation state changes. */
  onValidation?: (valid: boolean) => void
  /** Max characters. default is 1000 */
  maxLength?: number
}

/**
 * A controlled text input component that supports various features such as validation, icons, and custom styling.
 */
export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      placeholder,
      value,
      className,
      onChange = () => {},
      onSubmit,
      disabled,
      small,
      nooutline,
      onFocusChange,
      onKeyDown,
      icon,
      rightIcons,
      onPaste,
      invalid,
      clearable,
      blue,
      type = 'text',
      autocomplete,
      growToContent,
      validation,
      onValidation,
      maxLength = 1000,
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const spanRef = React.useRef<HTMLSpanElement | null>(null)

    React.useEffect(() => {
      if (growToContent && inputRef.current && spanRef.current) {
        spanRef.current.textContent = value || placeholder || ''
        inputRef.current.style.width = `${spanRef.current.offsetWidth + 8}px`
      }
    }, [value, placeholder, growToContent])

    const onChangeWrapper = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value || ''
      if (onChange && newValue !== value) onChange(newValue.slice(0, maxLength))
    }

    const valid = React.useMemo(() => {
      try {
        if (value.trim().length === 0) return true
        if (validation === 'email') return isEmail(value)
        if (validation === 'phone') return isMobilePhone(value, 'any')
        if (validation === 'url') return isURL(value)
      } catch (e) {
        console.error('Validation error:', e)
      }
      return true
    }, [value, validation])
    const validationClass = !valid && !disabled && !invalid ? 'outline outline-warning focus:ring-warning' : ''
    React.useEffect(() => {
      if (onValidation) onValidation(valid)
    }, [valid, onValidation])

    const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : ''
    const sizeClass = small ? 'text-sm px-2 py-[2.5px]' : `py-[6px] ${icon ? 'pl-10' : ''}`
    const invalidClass = invalid ? 'outline outline-red-500 focus:ring-red-500' : ''

    return (
      <InputWrapper
        innerClasses={className}
        rightIcons={
          <>
            {rightIcons}
            {clearable && value && value.length > 0 && (
              <div className={ButtonStyle.close} onClick={() => onChange('')}>
                <IconTimes thicken={40} />
              </div>
            )}
          </>
        }
        icon={icon}
      >
        {/* Hidden span to measure text width */}
        {growToContent && (
          <span ref={spanRef} className="invisible absolute whitespace-pre px-2 py-[6px]">
            {value || placeholder}
          </span>
        )}

        <input
          id={autocomplete ? `autocomplete-${autocomplete}` : undefined}
          name={autocomplete ? `autocomplete-${autocomplete}` : undefined}
          disabled={disabled}
          ref={(el) => {
            inputRef.current = el
            if (typeof ref === 'function') ref(el)
            else if (ref) ref.current = el
          }}
          className={`w-full-in-wrapper rounded placeholder-gray-400 transition-colors duration-150 focus:outline-none ${
            nooutline
              ? '!border-transparent bg-transparent !p-0 !ring-transparent'
              : 'border-gray-200 focus:border-gray-200 focus:ring-2 focus:ring-gray-200 focus:ring-opacity-50'
          } ${className} ${blue ? BlueInput : ''} ${disabledClass} ${sizeClass} ${invalidClass} ${validationClass}`}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChangeWrapper}
          onFocus={() => onFocusChange && onFocusChange(true)}
          onBlur={() => onFocusChange && onFocusChange(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onSubmit) onSubmit()
            else if (onKeyDown) onKeyDown(e)
          }}
          onPaste={(e) => {
            const value = e.clipboardData.getData('text')
            if (value && value.length && onPaste) {
              if (!onPaste(value)) {
                e.preventDefault()
                e.stopPropagation()
              }
            }
          }}
          style={growToContent ? { width: 'auto', minWidth: '10px' } : {}}
        />
      </InputWrapper>
    )
  }
)

interface DelayedTextInputProps extends TextInputProps {
  /** The delay in milliseconds before the onChange callback is triggered. Defaults to 300ms. */
  delay?: number
}

/**
 * A controlled text input component that delays the onChange callback by a specified amount of time.
 * This is useful for reducing the number of updates when the user is typing quickly.
 */
export const DelayedTextInput = React.forwardRef<HTMLInputElement, DelayedTextInputProps>(
  ({ delay = 300, onChange = () => {}, value: propValue, ...rest }, ref) => {
    const [value, setValue] = useBufferedUpdates(propValue || '', onChange, delay)
    return <TextInput {...rest} ref={ref} value={value} onChange={setValue} />
  }
)

interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  /** Additional CSS classes for the textarea. */
  className?: string
  /** Callback function triggered when the textarea value changes. */
  onChange: (v: string) => void
  /** The current value of the textarea. */
  value: string
  /** The placeholder text for the textarea. */
  placeholder?: string
}

/**
 * A controlled textarea component that supports various features such as resizing, icons, and custom styling.
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, onChange, value, placeholder, ...props }, ref) => {
    return (
      <textarea
        {...props}
        className={`w-full rounded border-gray-200 px-2 py-1 placeholder-gray-400 focus:border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-opacity-50 ${className}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        ref={ref}
      />
    )
  }
)

export const BlueInput = 'bg-lightBlue !border-none shadow-even-inset'
export const CharcoalInput =
  'bg-charcoal-hover focus:bg-charcoal-light !border-none placeholder:text-charcoal-text focus:ring-charcoal-light/30'

type InputWrapperProps = {
  innerClasses?: string
  className?: string
  rightIcons?: React.ReactNode
  icon?: React.ReactNode
  children: React.ReactNode
}

export const InputWrapper = React.forwardRef<HTMLDivElement, InputWrapperProps>(
  ({ innerClasses, rightIcons, icon, children, className }, ref) => {
    const outerClasses = [
      'flex-grow',
      'self-stretch',
      'ml-auto',
      'w-full',
      'self-start',
      'self-center',
      'self-end',
      'mr-auto',
    ]
      .filter((x) => innerClasses?.includes(x))
      .join(' ')

    if (isNullOrEmpty(rightIcons) && isNullOrEmpty(icon)) {
      return <>{children}</>
    }

    return (
      <div ref={ref} className={`wrapper relative z-10 flex ${outerClasses} ${className}`}>
        {icon && <div className="absolute bottom-0 left-3 top-0 flex items-center text-charcoal-text">{icon}</div>}
        {children}
        {rightIcons && <div className="absolute bottom-0 right-2 top-0 z-10 flex items-center">{rightIcons}</div>}
      </div>
    )
  }
)

export const InputRightFlag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="-mr-2 flex gap-2 rounded-l border-b-2 border-l-2 border-t-2 border-warning bg-white px-2 py-1 shadow-[inset_-3px_0_2px_-2px_rgba(202,_202,_202,_0.25),inset_0_0_2px_0_rgba(161,_161,_161,_0.35)]">
    {children}
  </div>
)

type NumberInputProps = Omit<TextInputProps, 'type' | 'value' | 'onChange'> & {
  /** The minimum value for the number input. */
  min?: number
  /** The maximum value for the number input. */
  max?: number
  /** The current value of the number input. */
  value: number | undefined
  /** Callback function triggered when the number input value changes. */
  onChange: (v: number | undefined) => void
}

/**
 * A controlled number input component
 */
export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onChange, min = 0, max, ...rest }, ref) => {
    return (
      <TextInput
        ref={ref}
        value={value === undefined ? '' : value.toString()}
        onChange={(v) => {
          const num = parseInt(v)
          if (isNaN(num) || (min !== undefined && num < min) || (max !== undefined && num > max)) onChange(undefined)
          else onChange(num)
        }}
        type="number"
        {...rest}
      />
    )
  }
)

type DateInputProps = Omit<TextInputProps, 'type' | 'value' | 'onChange'> & {
  /** The current value of the date input. */
  value: Date | undefined
  /** Callback function triggered when the date input value changes. */
  onChange?: (v: Date | undefined) => void
}

/**
 * A controlled date input component
 */
export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(({ value, onChange, ...rest }, ref) => {
  return (
    <TextInput
      ref={ref}
      value={value ? format(value, 'yyyy-MM-dd') : ''}
      onChange={(v) => {
        if (v.length === 0) {
          onChange?.(undefined)
        } else {
          const date = new Date(v + 'T00:00')
          if (!isNaN(date.getTime())) onChange?.(date)
        }
      }}
      type="date"
      {...rest}
    />
  )
})
